import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useChatStore } from '@/store/chatStore';
import { groupSessionsByTime } from '@/lib/chatSessions';
import { PlusIcon, ChatBubbleLeftIcon, TrashIcon } from '@heroicons/react/24/outline';

interface ChatsListProps {
  /** Called after any action that should close the drawer (new chat, select). */
  onNavigate: () => void;
}

export const ChatsList: React.FC<ChatsListProps> = ({ onNavigate }) => {
  const {
    sessions,
    currentSessionId,
    isLoadingSessions,
    messages,
    setCurrentSession,
    createNewSession,
    deleteSession,
  } = useChatStore();
  const navigate = useNavigate();

  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const groups = React.useMemo(() => groupSessionsByTime(sessions), [sessions]);

  const handleNew = async () => {
    // Reuse an already-empty session instead of creating duplicate blanks.
    if (messages.length > 0) await createNewSession();
    navigate({ to: '/' });
    onNavigate();
  };

  const handleSelect = async (id: string) => {
    if (id !== currentSessionId) await setCurrentSession(id);
    navigate({ to: '/' });
    onNavigate();
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this chat? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteSession(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="px-3 pt-3">
        <button
          onClick={handleNew}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-on-primary transition-colors hover:bg-primary/90"
        >
          <PlusIcon className="h-4 w-4" />
          New chat
        </button>
      </div>

      <div className="mt-2 flex-1 overflow-y-auto px-2 pb-3">
        {isLoadingSessions ? (
          <div className="flex justify-center py-10">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-text-muted">No chats yet</p>
        ) : (
          groups.map((group) => (
            <div key={group.label} className="mb-3">
              <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.sessions.map((s) => {
                  const active = s.id === currentSessionId;
                  return (
                    <li key={s.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelect(s.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSelect(s.id); }}
                        className={`group flex items-center gap-2.5 rounded-lg px-3 py-2 transition-colors ${
                          active ? 'bg-primary/10 text-primary' : 'text-text-secondary hover:bg-surface-container-high/50'
                        } ${deletingId === s.id ? 'opacity-50' : ''}`}
                      >
                        <ChatBubbleLeftIcon className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 truncate text-[13px]">{s.title}</span>
                        <button
                          onClick={(e) => handleDelete(s.id, e)}
                          disabled={deletingId === s.id}
                          aria-label="Delete chat"
                          className="rounded p-1 text-text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover:opacity-100"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
